/**
 * NOW: one line per Agent working on the ticket's pull request right now
 * (YAG-1079). Proctor reviewing and Fletcher testing are activity inside the
 * ticket, never its header, and both show when both are working, each naming
 * the head it is on: "Proctor is reviewing 800d063", "Fletcher is testing
 * 800d063". Watch opens that Agent's own record.
 *
 * The ticket's own run (Reeve planning, Wright building or fixing) is the live
 * stream below, so it is not a line here.
 */
import type { WorkItem, WorkerSummary } from "@yagni/shared";
import { Btn } from "../queue/primitives.js";
import { WorkerMark } from "../workers/WorkerMark.js";
import { startedClock } from "../work/ProctorStatus.js";

export interface NowLine {
  key: "proctor" | "fletcher";
  worker: WorkerSummary | null;
  text: string;
  meta: string | null;
  /** Opens the Agent's own record; absent when the sheet cannot name it. */
  onWatch?: () => void;
}

/** "800d063", or the pull request when the head is not known. */
function headWords(sha: string | null | undefined, prNumber: number | null | undefined): string {
  if (sha) return sha.slice(0, 7);
  return prNumber != null ? `PR #${prNumber}` : "the pull request";
}

function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  const suffix = { 1: "st", 2: "nd", 3: "rd" }[n % 10] ?? "th";
  return `${n}${suffix}`;
}

/** Proctor's line while it reviews: the head, when it started, and which review of the pull request it is. */
export function proctorNowLine(facts: {
  worker: WorkerSummary | null;
  reviewer: string;
  headSha: string | null | undefined;
  prNumber: number | null | undefined;
  startedAt: string | null | undefined;
  attempt: number | null | undefined;
  onWatch?: () => void;
}): NowLine {
  const started = startedClock(facts.startedAt);
  const meta = [
    started ? `started ${started}` : null,
    typeof facts.attempt === "number" && facts.attempt > 1
      ? `${ordinal(facts.attempt)} review of this pull request`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return {
    key: "proctor",
    worker: facts.worker,
    text: `${facts.reviewer} is reviewing ${headWords(facts.headSha, facts.prNumber)}`,
    meta: meta || null,
    ...(facts.onWatch ? { onWatch: facts.onWatch } : {}),
  };
}

/** Fletcher's line while its check runs: the head it tests and when it started. */
export function fletcherNowLine(facts: {
  worker: WorkerSummary | null;
  tester: string;
  check: WorkItem;
  prNumber: number | null | undefined;
  onWatch?: () => void;
}): NowLine {
  const started = startedClock(facts.check.activeRun?.startedAt ?? facts.check.latestRunStartedAt);
  return {
    key: "fletcher",
    worker: facts.worker,
    text: `${facts.tester} is testing ${headWords(facts.check.qaHeadSha, facts.prNumber)}`,
    meta: started ? `started ${started}` : null,
    ...(facts.onWatch ? { onWatch: facts.onWatch } : {}),
  };
}

export function NowLines({ lines }: { lines: readonly NowLine[] }) {
  if (lines.length === 0) return null;
  return (
    <div className="blk" data-testid="ticket-now">
      <div className="bh">
        <span className="label">Now</span>
      </div>
      {lines.map((line) => (
        <div key={line.key} className="nowline" data-testid="ticket-now-line" data-who={line.key}>
          <span className="nowline-dot" aria-hidden />
          {line.worker && (
            <WorkerMark
              avatar={line.worker.avatar}
              name={line.worker.displayName}
              slug={line.worker.slug}
              size="sm"
            />
          )}
          <span className="nowline-text">
            <span className="nowline-what">{line.text}</span>
            {line.meta && <span className="nowline-meta">{line.meta}</span>}
          </span>
          {line.onWatch && (
            <Btn variant="quiet" size="sm" onClick={line.onWatch}>
              Watch
            </Btn>
          )}
        </div>
      ))}
    </div>
  );
}
